import { useLazyQuery, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { StepperLayoutTabs } from 'layouts/CreateStepperLayout';

import { getUser } from 'utils/auth';

const GET_USER_PROFILE_QUERY = gql(`
  query GetUserOnboardingUser($id: ID!) {
    user(id: $id) {
      id
      bio
      investmentEntities {
        id
        status
      }
    }
  }
`);

const GET_INVESTMENT_ENTITY_QUERY = gql(`
  query GetUserOnboardingInvestmentEntity($id: ID!) {
    investmentEntity(id: $id) {
      id
      kyc {
        id
      }
      investorApplication {
        id
      }
    }
  }
`);

const RUVOnboardingTabs: FC<{
  isUserWithActiveEntity: boolean;
}> = ({ isUserWithActiveEntity }) => {
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>();

  const user = getUser();

  const { data: userData } = useQuery(GET_USER_PROFILE_QUERY, {
    variables: { id: user.id },
  });

  const [getInvestmentEntity, { data: entityData }] = useLazyQuery(GET_INVESTMENT_ENTITY_QUERY);

  useEffect(() => {
    if (!investmentEntityId) return;

    getInvestmentEntity({ variables: { id: investmentEntityId } });
  }, [investmentEntityId, getInvestmentEntity]);

  const baseUrl = '/onboarding/complete-profile-ruv';

  const tabsForExistingUsers = [
    {
      name: 'Social Profile',
      matchingUrls: [
        baseUrl,
        `${baseUrl}/social-profile`,
        `${baseUrl}/social-profile/${investmentEntityId}`,
      ],
      defaultUrl: investmentEntityId
        ? `${baseUrl}/social-profile/${investmentEntityId}`
        : `${baseUrl}/social-profile`,
      title: 'Tell us a little more about yourself',
      subTitle: 'This will help other investors, funds and startups know more about you',
      conditionForComplete: !!userData?.user?.bio,
      index: true,
    },
  ];

  const tabsForNewUsers = [
    {
      name: 'Founder Profile',
      matchingUrls: [
        baseUrl,
        `${baseUrl}/social-profile`,
        `${baseUrl}/social-profile/${investmentEntityId}`,
      ],
      defaultUrl: investmentEntityId
        ? `${baseUrl}/social-profile/${investmentEntityId}`
        : `${baseUrl}/social-profile`,
      title: 'Tell us a little more about yourself',
      subTitle: 'This will help other investors, funds and startups know more about you',
      conditionForComplete: !!userData?.user?.bio,
      index: true,
    },
    {
      name: 'Founder KYC',
      matchingUrls: [`${baseUrl}/investment-entity/${investmentEntityId}/kyc`],
      defaultUrl: `${baseUrl}/investment-entity/${investmentEntityId}/kyc`,
      title: 'KYC',
      subTitle: 'Regulations require us to verify your identity',
      conditionForComplete: !!entityData?.investmentEntity?.kyc,
    },
    {
      name: 'Company Profile',
      matchingUrls: [`${baseUrl}/create-syndicate/ruv/${investmentEntityId}`],
      defaultUrl: `${baseUrl}/create-syndicate/ruv/${investmentEntityId}`,
      title: 'Company Profile',
      subTitle: '',
      conditionForComplete: undefined,
    },
  ];

  return (
    <StepperLayoutTabs tabs={isUserWithActiveEntity ? tabsForExistingUsers : tabsForNewUsers} />
  );
};

export default RUVOnboardingTabs;
